"use client";

import { useState, useMemo } from "react";
import { type AiAnalyticsFilters, type TimeRange, type BotTypeOption } from "@saas/ai-analytics/lib/api";
import { useAiBotTypesDemo, useAiTimelineWithTrendsDemo } from "@marketing/ai-analytics-live/lib/api";
import { useAiTimelineByIntentDemo } from "@marketing/ai-analytics-live/lib/api";
import { type PageIntent } from "@repo/core/client";
import { AnalyticsLoadingState } from "@saas/shared/components/AnalyticsLoadingState";
import { AnalyticsErrorState } from "@saas/shared/components/AnalyticsErrorState";
import { AnalyticsNoDataState } from "@saas/shared/components/AnalyticsNoDataState";
import { AnalyticsFilters } from "@saas/shared/components/AnalyticsFilters";
import { DateRange, TimeRangeType } from "@saas/shared/components/DateRangeFilter";
import { AiSummaryWithTrends } from "@saas/ai-analytics/components/AiSummaryWithTrends";
import { AiTrafficTimeline } from "@saas/ai-analytics/components/AiTrafficTimeline";
import { useTranslations } from "next-intl";

interface InteractiveAiAnalyticsDemoProps {
  propertyApiKey: string;
}

export function InteractiveAiAnalyticsDemo({ propertyApiKey }: InteractiveAiAnalyticsDemoProps) {
  const t = useTranslations("marketing.aiAnalyticsLive");
  // State management (same pattern as AiAnalytics component)
  const [timeRange, setTimeRange] = useState<TimeRangeType>("7d");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  
  // Page and model filters
  const [selectedPagePath, setSelectedPagePath] = useState<string | undefined>();
  const [selectedBotTypeId, setSelectedBotTypeId] = useState<string | undefined>();
  
  // Intent filters
  const [selectedIntents, setSelectedIntents] = useState<PageIntent[]>([]);

  // Convert time range to date range
  const dateRange = useMemo<TimeRange>(() => {
    if (timeRange === 'live') {
      // For live data, show last 30 minutes
      const end = new Date();
      const start = new Date(end.getTime() - 30 * 60 * 1000);
      return { start, end };
    }
    
    if (timeRange === 'custom' && customDateRange?.from && customDateRange?.to) {
      return {
        start: customDateRange.from,
        end: customDateRange.to,
      };
    }
    
    // Parse time range
    const now = new Date();
    const match = timeRange.match(/^(\d+)([hdw])$/);
    if (!match) {
      // Default to 7 days
      return {
        start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: now,
      };
    }
    
    const [, value, unit] = match;
    const num = parseInt(value, 10);
    let milliseconds = 0;
    
    switch (unit) {
      case 'h':
        milliseconds = num * 60 * 60 * 1000;
        break;
      case 'd':
        milliseconds = num * 24 * 60 * 60 * 1000;
        break;
      case 'w':
        milliseconds = num * 7 * 24 * 60 * 60 * 1000;
        break;
    }
    
    return {
      start: new Date(now.getTime() - milliseconds),
      end: now,
    };
  }, [timeRange, customDateRange]);

  // Create time range label for trend display
  const timeRangeLabel = useMemo(() => {
    if (timeRange === 'live') return 'last 30 minutes';
    if (timeRange === 'custom') return 'custom period';
    
    const match = timeRange.match(/^(\d+)([hdw])$/);
    if (!match) return 'last 7 days';
    
    const [, value, unit] = match;
    const num = parseInt(value, 10);
    
    switch (unit) {
      case 'h':
        return num === 1 ? 'this hour' : `last ${num} hours`;
      case 'd':
        return num === 1 ? 'today' : `last ${num} days`;
      case 'w':
        return num === 1 ? 'this week' : `last ${num} weeks`;
      default:
        return 'last 7 days';
    }
  }, [timeRange]);

  // Create filters object for API calls
  const filters = useMemo<AiAnalyticsFilters>(() => {
    const baseFilters: AiAnalyticsFilters = {
      pagePath: selectedPagePath,
      botTypeId: selectedBotTypeId,
    };
    
    // Add intent filters
    if (selectedIntents.length > 0) {
      baseFilters.intents = selectedIntents;
    }
    
    return baseFilters;
  }, [
    selectedPagePath, 
    selectedBotTypeId,
    selectedIntents
  ]);

  // Get data using demo-specific hooks
  const { data: botTypes, isLoading: isBotTypesLoading } = useAiBotTypesDemo(propertyApiKey);

  const { data: timelineWithTrends, isLoading: isTimelineLoading, error: timelineError } = useAiTimelineWithTrendsDemo(
    propertyApiKey, // Use the demo property API key directly
    dateRange,
    timeRange === "24h" || timeRange === "live" ? "hour" : "day",
    filters
  );

  // Always fetch intent-based timeline data for default view
  const { data: intentTimelineData, isLoading: isIntentTimelineLoading, error: intentTimelineError } = useAiTimelineByIntentDemo(
    propertyApiKey,
    dateRange,
    timeRange === "24h" || timeRange === "live" ? "hour" : "day",
    filters // This includes selectedIntents for filtering
  );

  // Build filter info for export
  const filterInfo = useMemo(() => {
    const selectedBotType = botTypes?.find((bt: BotTypeOption) => bt.id === selectedBotTypeId);
    
    return {
      timeRange: timeRangeLabel,
      pagePath: selectedPagePath,
      aiModel: selectedBotType ? (selectedBotType.company || selectedBotType.name) : undefined,
      intents: selectedIntents.length > 0 ? selectedIntents : undefined,
    };
  }, [
    timeRangeLabel,
    selectedPagePath,
    selectedBotTypeId,
    botTypes,
    selectedIntents
  ]);

  return (
    <div className="space-y-8">
      {/* Interactive Filters */}
      <div className="rounded-2xl border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">{t("filters.title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("filters.description")}
          </p>
        </div>
        
        <AnalyticsFilters
          propertyId=""
          propertyApiKey={propertyApiKey}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={setCustomDateRange}
          selectedPagePath={selectedPagePath}
          onPagePathChange={setSelectedPagePath}
          selectedIntents={selectedIntents}
          onIntentsChange={setSelectedIntents}
          selectedAiModel={selectedBotTypeId}
          onAiModelChange={setSelectedBotTypeId}
          aiModelOptions={botTypes}
          isLoading={isTimelineLoading}
          isLive={timeRange === 'live'}
        />
      </div>

      {/* Data Section - Conditionally rendered */}
      {isTimelineLoading || isIntentTimelineLoading ? (
        <AnalyticsLoadingState />
      ) : timelineError || intentTimelineError ? (
        <AnalyticsErrorState error={timelineError?.message || intentTimelineError?.message || "Unknown error"} />
      ) : !timelineWithTrends || timelineWithTrends.timeline.length === 0 ? (
        <AnalyticsNoDataState />
      ) : (
        <>
          {/* Summary Metrics with Trends */}
          <AiSummaryWithTrends 
            data={timelineWithTrends} 
            timeRangeLabel={timeRangeLabel}
          />
          
          {/* Main Timeline */}
          <AiTrafficTimeline 
            data={timelineWithTrends.timeline}
            isLoading={isTimelineLoading || isIntentTimelineLoading}
            showDetailed={true}
            filterInfo={filterInfo}
            intentData={intentTimelineData}
            showByIntent={true}
          />
        </>
      )}
    </div>
  );
} 